import { Button, ButtonTheme } from 'components/Button';
import { ICONS } from 'components/Icon';
import { Menu } from 'components/Menu';
import { Pressable } from 'components/Pressable';
import { useSelector } from 'hooks/useSelector';
import { resizeToWidthHeight } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { getDateFormattedForFilename } from 'service/file';
import { SET_GENERIC } from 'store/actions';
import { selectGeneric } from 'store/app/selectors';
import { captureException } from '@sentry/react-native';
import { useEsc } from 'hooks/useEsc';
import { Image } from 'expo-image';

const ButtonsHeight = 125;

type File = { name: string; uri: string; width: number; height: number };

export const Camera: React.FC<{
  canvasWidth: number;
  canvasHeight: number;
  onUsePhoto: (file: File) => void;
  onCancel: (file: File | undefined) => void;
}> = ({ canvasWidth, canvasHeight, onUsePhoto, onCancel }) => {
  const dispatch = useDispatch();

  const selectedDeviceId = useSelector((state) =>
    selectGeneric(state, 'cameraSelectedDeviceId'),
  );

  const [stream, setStream] = React.useState<MediaStream>();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [draftPhoto, setDraftPhoto] = React.useState<
    { name: string; uri: string; width: number; height: number } | undefined
  >();

  const photoWidthHeightRef = React.useRef({ width: 0, height: 0 });
  const cameraWidthHeightRef = React.useRef({ width: 0, height: 0 });

  const devicesRef = React.useRef<
    { id: string; name: string; width: number; height: number }[]
  >([]);
  const device =
    (selectedDeviceId
      ? devicesRef.current.find((d) => d.id === selectedDeviceId)
      : undefined) || devicesRef.current[0];

  cameraWidthHeightRef.current = React.useMemo<{
    width: number;
    height: number;
  }>(() => {
    return device
      ? resizeToWidthHeight({
          width: device.width,
          height: device.height,
          outgoingWidth: canvasWidth,
          outgoingHeight: canvasHeight,
        })
      : { width: 0, height: 0 };
  }, [canvasHeight, canvasWidth, device]);

  photoWidthHeightRef.current = React.useMemo<{
    width: number;
    height: number;
  }>(() => {
    return draftPhoto
      ? resizeToWidthHeight({
          width: draftPhoto.width,
          height: draftPhoto.height,
          outgoingWidth: Math.min(
            cameraWidthHeightRef.current.width,
            canvasWidth,
          ),
          outgoingHeight: canvasHeight,
        })
      : { width: 0, height: 0 };
  }, [canvasHeight, canvasWidth, draftPhoto]);

  function handleError(error: Error) {
    captureException(error);
  }

  const handleGotDevices = React.useCallback(
    (deviceInfos: (MediaDeviceInfo | InputDeviceInfo)[]) => {
      const draftDevices: typeof devicesRef.current = [];
      for (let i = 0; i < deviceInfos.length; i += 1) {
        const deviceInfo = deviceInfos[i]!;
        if (deviceInfo.kind === 'videoinput') {
          /** @ts-expect-error deviceInfo may be InputDeviceInfo which has getCapabilities */
          const capabilities = deviceInfo?.getCapabilities();
          draftDevices.push({
            id: deviceInfo.deviceId,
            name: deviceInfo.label,
            width: capabilities?.width?.max || 640,
            height: capabilities?.height?.max || 480,
          });
        }
      }
      devicesRef.current = draftDevices;
    },
    [],
  );

  const handleTakePhoto = React.useCallback(() => {
    if (!videoRef.current) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      return;
    }
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    setDraftPhoto({
      name: `Image - ${getDateFormattedForFilename()}.jpeg`,
      width: canvas.width,
      height: canvas.height,
      uri: canvas.toDataURL('image/jpeg'),
    });
  }, [videoRef]);

  React.useEffect(() => {
    if (videoRef.current && stream) {
      /** @ts-expect-error window.stream exists */
      window.stream = stream;
      videoRef.current.srcObject = stream;
    }
  }, [stream, videoRef]);

  React.useEffect(() => {
    if (!device) {
      navigator.mediaDevices
        .enumerateDevices()
        .then(handleGotDevices)
        .catch(handleError);
    } else {
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: { exact: device.id },
          width: { exact: device.width },
        },
        audio: false,
      };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(setStream)
        .catch(handleError);
    }
  }, [device, handleGotDevices, selectedDeviceId]);

  useEsc(() => onCancel(draftPhoto));

  return (
    <View>
      {draftPhoto ? (
        <Image
          source={{ uri: draftPhoto.uri }}
          style={{
            alignSelf: 'center',
            width: photoWidthHeightRef.current.width,
            height: photoWidthHeightRef.current.height,
          }}
        />
      ) : (
        <View
          style={{
            alignSelf: 'center',
            width: cameraWidthHeightRef.current.width,
          }}
        >
          <video ref={videoRef} autoPlay />
        </View>
      )}
      {draftPhoto ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            height: ButtonsHeight,
            paddingHorizontal: theme.paddingHorizontal * 2,
          }}
        >
          <View style={{ flex: 1 }}>
            <Button
              text="Retake"
              style={{ alignSelf: 'flex-end' }}
              buttonTheme={ButtonTheme.themeless}
              textStyle={{ color: 'white' }}
              textSize="xlarge"
              textWeight="regular"
              onPress={() => setDraftPhoto(undefined)}
            />
          </View>
          <View style={{ width: 200 }} />
          <View style={{ flex: 1 }}>
            <Button
              text="Use Photo"
              buttonTheme={ButtonTheme.themeless}
              textStyle={{ color: 'white' }}
              textSize="xlarge"
              textWeight="regular"
              onPress={() => onUsePhoto(draftPhoto)}
            />
          </View>
        </View>
      ) : (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'center',
            height: ButtonsHeight,
            paddingHorizontal: theme.paddingHorizontal * 2,
          }}
        >
          <View style={{ flex: 1, alignItems: 'flex-end' }}>
            <Button
              text="Cancel"
              buttonTheme={ButtonTheme.themeless}
              textStyle={{ color: 'white' }}
              textSize="xlarge"
              textWeight="regular"
              onPress={() => onCancel(draftPhoto)}
            />
          </View>
          <View style={{ width: 200, alignItems: 'center' }}>
            <Pressable
              onPress={handleTakePhoto}
              style={{
                width: 84,
                height: 84,
                borderRadius: 84,
                backgroundColor: 'white',
                alignItems: 'center',
                justifyContent: 'center',
                borderWidth: 2,
                borderColor: 'black',
              }}
              hoveredStyle={{
                backgroundColor: theme.colors.white80,
              }}
              pressedStyle={{
                backgroundColor: theme.colors.white50,
              }}
            >
              <View
                style={{
                  width: 66,
                  height: 66,
                  borderRadius: 66,
                  borderWidth: 2,
                  borderColor: 'black',
                }}
              />
            </Pressable>
          </View>
          <View style={{ flex: 1 }}>
            {devicesRef.current.length > 1 ? (
              <Menu
                iconButtonConfig={{
                  icon: ICONS.CameraRotateDuotone,
                  style: {
                    marginLeft: theme.paddingHorizontal,
                    backgroundColor: theme.colors.white50,
                  },
                  hoveredStyle: {
                    backgroundColor: theme.colors.white30,
                  },
                  pressedStyle: {
                    backgroundColor: theme.colors.white20,
                  },
                  size: 60,
                  iconSize: 32,
                  color: 'white',
                }}
                options={devicesRef.current.map((d) => ({
                  text: d.name,
                  onPress: () =>
                    dispatch(
                      SET_GENERIC({
                        key: 'cameraSelectedDeviceId',
                        value: d.id,
                      }),
                    ),
                }))}
              />
            ) : (
              <Button
                text="Use Photo"
                style={{ alignSelf: 'flex-start' }}
                buttonTheme={ButtonTheme.themeless}
                textStyle={{ color: 'white' }}
                textSize="xlarge"
                textWeight="regular"
              />
            )}
          </View>
        </View>
      )}
    </View>
  );
};
